// SETTINGS > DUTY RATES -- ADMIN ONLY (duty-rate-entry, 2026-09-30).
//
// ⚠️ A PRICING INPUT, SO THIS IS BUILT LIKE THE FX SCREEN. The duty on an
// imported line is resolved from this schedule; a Settings screen that could
// edit it freely would be a route to a pricing input by the back door. So:
// Admin only, a new row every time (publishing closes the HSN's open window at
// the day before), nothing ever deleted, a correction only for a rate that has
// not started and that no finalised quote used, and the author recorded from
// the session.
//
// ⚠️ THE SCREEN IS NOT THE GUARD. It is rendered only for an Admin, but that is
// cosmetic: every /anixify/api/*duty* write route refuses Sales and Viewer with
// a 403 whatever this does, and the backend makes every check below as well.
// What the backend says is shown verbatim.
//
// ⚠️ NOTHING IS COMPUTED HERE. The typo guard compares each typed percentage
// with the row in force for that HSN, field by field -- it never combines BCD
// and SWS. The upload preview's "% change" is the server's own figure.
//
// ⚠️ AN UPLOAD SAVES NOTHING UNTIL CONFIRMED, and confirming is all-or-nothing:
// one bad row and the confirm is disabled (and the server would refuse it
// anyway). Type percentages as numbers: 10 means 10 %.

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  listDutyRates, saveDutyRate, saveDutyRates, previewDutyUpload, correctDutyRate,
  type DutyPreviewRow, type DutyRateInput,
} from "@/services/api";
import { TYPO_GUARD_PCT, percentDifferent, todayIso } from "@/services/fxInForce";
import type { DutyRate } from "@/types";
import { formatDate } from "@/lib/format";
import { useNeedsSideways } from "@/lib/useNeedsSideways";
import {
  colors, semanticColors, radius, spacing, heights, layout, typography, shadows,
} from "@/styles/tokens";

const INPUT: React.CSSProperties = {
  height: heights.input, padding: `0 ${spacing[8]}px`,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  fontSize: typography.fontSize.base, backgroundColor: semanticColors.surface,
  color: semanticColors.textPrimary, boxSizing: "border-box",
};
const BUTTON: React.CSSProperties = {
  height: heights.control, padding: `0 ${spacing[14]}px`,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  fontSize: typography.fontSize.base, backgroundColor: semanticColors.surface,
  color: semanticColors.textPrimary, cursor: "pointer", whiteSpace: "nowrap",
};
const PRIMARY: React.CSSProperties = {
  ...BUTTON, border: "none", backgroundColor: colors.indigo600,
  color: semanticColors.onAccent, fontWeight: typography.fontWeight.medium,
};
const BOX: React.CSSProperties = {
  marginBottom: spacing[12], padding: spacing[12],
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  backgroundColor: semanticColors.panelBg,
  display: "flex", flexDirection: "column", gap: spacing[8],
};
const NOTE: React.CSSProperties = {
  fontSize: typography.fontSize.sm, color: semanticColors.textSecondary,
};
const TH: React.CSSProperties = {
  textAlign: "left", padding: `${spacing[6]}px ${spacing[8]}px`,
  fontSize: typography.fontSize.xs, color: semanticColors.textMuted,
  fontWeight: typography.fontWeight.medium, borderBottom: `1px solid ${semanticColors.border}`,
  whiteSpace: "nowrap",
};
const TD: React.CSSProperties = {
  padding: `${spacing[6]}px ${spacing[8]}px`, fontSize: typography.fontSize.base,
  borderBottom: `1px solid ${semanticColors.border}`, whiteSpace: "nowrap",
};

/** The 8-digit rule, stated once and mirrored by the backend (the authority). */
const HSN_RE = /^\d{8}$/;

/** A row's standing today: in force, not yet started, or ended. */
function standing(r: DutyRate, today: string): "In force" | "Starts later" | "Ended" | "Withdrawn" {
  if (!r.active) return "Withdrawn";
  if (r.validTo && r.validTo < today) return "Ended";
  if (r.validFrom && r.validFrom > today) return "Starts later";
  return "In force";
}

/** The single active row in force for `hsn` today, or null (none or several). */
function inForceFor(rates: DutyRate[], hsn: string, today: string): DutyRate | null {
  const rows = rates.filter((r) => r.hsnCode === hsn && standing(r, today) === "In force");
  return rows.length === 1 ? rows[0] : null;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("The file could not be read."));
    reader.onload = () => {
      const text = String(reader.result ?? "");
      resolve(text.slice(text.indexOf(",") + 1));
    };
    reader.readAsDataURL(file);
  });
}

export function DutyRatesTab() {
  const today = todayIso();
  const [rates, setRates] = useState<DutyRate[]>([]);
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const result = await listDutyRates();
    setLoading(false);
    if (!result.ok || !result.rates) {
      setRates([]);
      setLoadError(result.error ?? "The duty schedule could not be loaded.");
      return;
    }
    setLoadError("");
    setRates(result.rates);
  }, []);
  useEffect(() => { void load(); }, [load]);

  // ---- publish one --------------------------------------------------------
  const [hsn, setHsn] = useState("");
  const [bcd, setBcd] = useState("");
  const [sws, setSws] = useState("");
  const [igst, setIgst] = useState("");
  const [from, setFrom] = useState(today);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  const [typoNote, setTypoNote] = useState("");

  const current = HSN_RE.test(hsn) ? inForceFor(rates, hsn, today) : null;

  const publish = async (confirmed: boolean) => {
    setError(""); setDone("");
    if (!HSN_RE.test(hsn)) { setError("HSN must be exactly 8 digits."); return; }
    for (const [label, v] of [["BCD %", bcd], ["SWS %", sws], ["IGST %", igst]] as const) {
      const n = Number(v);
      if (v.trim() === "" || !Number.isFinite(n) || n < 0 || n > 100) {
        setError(`${label} must be a number between 0 and 100 (0 is allowed).`);
        return;
      }
    }
    if (!from) { setError("Give the date this rate applies from."); return; }
    if (from < today) { setError("A rate cannot start in the past."); return; }

    // TYPO GUARD: warns, never blocks. Field by field against the row in force.
    if (!confirmed && current) {
      const moved = ([["BCD", Number(bcd), current.bcdPct], ["SWS", Number(sws), current.swsPct],
                      ["IGST", Number(igst), current.igstPct]] as const)
        .map(([name, now, before]) => {
          if (before === 0 && now !== 0) return `${name} ${before} → ${now}`;
          const pct = percentDifferent(now, before);
          return pct !== null && pct > TYPO_GUARD_PCT ? `${name} ${before} → ${now} (${pct.toFixed(0)}%)` : null;
        })
        .filter(Boolean);
      if (moved.length > 0) {
        setTypoNote(`More than ${TYPO_GUARD_PCT}% different from the rate in force: ${moved.join(", ")}. Confirm?`);
        return;
      }
    }

    setBusy(true);
    const result = await saveDutyRate({
      hsn, bcd_pct: bcd, sws_pct: sws, igst_pct: igst, effective_from: from,
    });
    setBusy(false);
    setTypoNote("");
    if (!result.ok) { setError(result.error ?? "The rate was not published."); return; }
    setDone(`Published ${hsn} from ${from}.${current ? " The previous rate now ends the day before." : ""}`);
    setHsn(""); setBcd(""); setSws(""); setIgst("");
    void load();
  };

  // ---- upload --------------------------------------------------------------
  const [preview, setPreview] = useState<DutyPreviewRow[] | null>(null);
  const [previewName, setPreviewName] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [uploadDone, setUploadDone] = useState("");
  const [ack, setAck] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const onFile = async (file: File | undefined) => {
    setUploadError(""); setUploadDone(""); setPreview(null); setAck(false);
    if (!file) return;
    try {
      const result = await previewDutyUpload(file.name, await fileToBase64(file));
      if (!result.ok || !result.rows) { setUploadError(result.error ?? "The file could not be previewed."); return; }
      setPreviewName(file.name);
      setPreview(result.rows);
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : "The file could not be read.");
    }
  };

  const errorCount = preview?.filter((r) => r.status === "error").length ?? 0;
  const flagged = preview?.filter((r) => r.large_change).length ?? 0;
  const canConfirm = !!preview && preview.length > 0 && errorCount === 0 && (flagged === 0 || ack) && !confirming;

  const confirm = async () => {
    if (!preview || !canConfirm) return;
    setConfirming(true); setUploadError("");
    const rows: DutyRateInput[] = preview.map((r) => ({
      hsn: r.hsn as string, bcd_pct: r.bcd_pct as number, sws_pct: r.sws_pct as number,
      igst_pct: r.igst_pct as number, effective_from: r.effective_from as string,
    }));
    const result = await saveDutyRates(rows);
    setConfirming(false);
    if (!result.ok) { setUploadError(result.error ?? "Nothing was saved."); return; }
    setUploadDone(`Saved ${result.count} rate${result.count === 1 ? "" : "s"} from ${previewName}.`);
    setPreview(null); setAck(false);
    void load();
  };

  // ---- correct -------------------------------------------------------------
  const [fixing, setFixing] = useState<DutyRate | null>(null);
  const [fixDone, setFixDone] = useState("");
  const [fixBcd, setFixBcd] = useState("");
  const [fixSws, setFixSws] = useState("");
  const [fixIgst, setFixIgst] = useState("");
  const [fixError, setFixError] = useState("");

  const startFix = (r: DutyRate) => {
    setFixing(r); setFixError(""); setFixDone("");
    setFixBcd(String(r.bcdPct)); setFixSws(String(r.swsPct)); setFixIgst(String(r.igstPct));
  };
  const saveFix = async (r: DutyRate) => {
    const result = await correctDutyRate(r.id, fixBcd, fixSws, fixIgst);
    if (!result.ok) { setFixError(result.error ?? "The rate was not corrected."); return; }
    setFixing(null);
    setFixDone(`Corrected ${r.hsnCode} from ${r.validFrom}: BCD ${fixBcd} / SWS ${fixSws} / IGST ${fixIgst}.`);
    void load();
  };

  // ---- layout (duty-rates-layout, owner 2026-09-30) ----------------------------
  const [publishOpen, setPublishOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [standingFilter, setStandingFilter] = useState("All");

  const sorted = useMemo(
    () => [...rates].sort((a, b) =>
      a.hsnCode.localeCompare(b.hsnCode) || (b.validFrom ?? "").localeCompare(a.validFrom ?? "")),
    [rates],
  );
  const shown = useMemo(() => {
    const q = search.trim();
    return sorted.filter((r) => (!q || r.hsnCode.includes(q))
      && (standingFilter === "All" || standing(r, today) === standingFilter));
  }, [sorted, search, standingFilter, today]);
  const counts = useMemo(() => {
    const c = { inForce: 0, later: 0, ended: 0 };
    for (const r of rates) {
      const st = standing(r, today);
      if (st === "In force") c.inForce += 1;
      else if (st === "Starts later") c.later += 1;
      else c.ended += 1;
    }
    return c;
  }, [rates, today]);
  const inForceHsns = useMemo(
    () => new Set(rates.filter((r) => standing(r, today) === "In force").map((r) => r.hsnCode)).size,
    [rates, today]);
  const TABLE_MIN = 860;
  const [tableRef, sideways] = useNeedsSideways(TABLE_MIN);

  return (
    <div data-testid="duty-rates-tab" style={{ display: "flex", flexDirection: "column" }}>
      {/* One line, the rest in the forms (duty-rates-layout). */}
      <div data-testid="duty-note" style={{ ...NOTE, marginBottom: spacing[12] }}>
        SWS is a percentage of the BCD (BCD 10 with SWS 10 is 11% of goods value); IGST is
        recorded for reference and never enters a price. Nothing is ever deleted.
      </div>

      {/* Compact summary + the two actions, like the Exchange Rates cards. */}
      <div data-testid="duty-summary" className="flex"
           style={{ gap: spacing[12], marginBottom: spacing[12], flexWrap: "wrap", alignItems: "stretch" }}>
        {([
          ["In force today", `${inForceHsns} HSN code${inForceHsns === 1 ? "" : "s"}`, "duty-summary-in-force"],
          ["Starting later", String(counts.later), "duty-summary-later"],
          ["Ended or withdrawn", String(counts.ended), "duty-summary-ended"],
        ] as const).map(([label, value, testid]) => (
          <div key={testid} data-testid={testid}
               style={{
                 flex: "1 1 200px", maxWidth: 220, padding: `${spacing[8]}px ${spacing[12]}px`,
                 backgroundColor: semanticColors.surface, border: `1px solid ${semanticColors.border}`,
                 borderRadius: radius.base, boxShadow: shadows.sm,
               }}>
            <div style={{ fontSize: typography.fontSize.xs, color: semanticColors.textSecondary }}>{label}</div>
            <div style={{ fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.semibold,
                          color: semanticColors.textPrimary, fontVariantNumeric: "tabular-nums" }}>{value}</div>
          </div>
        ))}
        <div className="flex items-center" style={{ gap: spacing[8] }}>
          <button data-testid="duty-publish-open" style={PRIMARY}
                  onClick={() => setPublishOpen((v) => !v)}>
            {publishOpen ? "Close" : "Publish rate"}
          </button>
          <button data-testid="duty-upload-open" style={BUTTON}
                  onClick={() => setUploadOpen((v) => !v)}>
            {uploadOpen ? "Close upload" : "Upload"}
          </button>
        </div>
      </div>

      {publishOpen && (
      <div data-testid="duty-publish" style={BOX}>
        <div style={NOTE}>
          <span style={{ fontWeight: typography.fontWeight.medium, color: semanticColors.textPrimary }}>
            Publish a duty rate.
          </span>{" "}
          A new rate is a new row: the one in force for that HSN ends the day before it
          starts. Nothing is deleted. SWS is a percentage of the BCD (BCD 10 with SWS 10 is 11%
          of goods value); IGST is recorded for reference and never enters a price.
        </div>
        <div style={{ display: "flex", gap: spacing[8], alignItems: "center", flexWrap: "wrap" }}>
          <input data-testid="duty-publish-hsn" placeholder="HSN (8 digits)" value={hsn}
                 onChange={(e) => { setHsn(e.target.value.trim()); setTypoNote(""); }}
                 style={{ ...INPUT, width: 140 }} />
          <input data-testid="duty-publish-bcd" type="number" step="any" min="0" max="100"
                 placeholder="BCD %" value={bcd}
                 onChange={(e) => { setBcd(e.target.value); setTypoNote(""); }}
                 style={{ ...INPUT, width: 90 }} />
          <input data-testid="duty-publish-sws" type="number" step="any" min="0" max="100"
                 placeholder="SWS %" value={sws}
                 onChange={(e) => { setSws(e.target.value); setTypoNote(""); }}
                 style={{ ...INPUT, width: 90 }} />
          <input data-testid="duty-publish-igst" type="number" step="any" min="0" max="100"
                 placeholder="IGST %" value={igst}
                 onChange={(e) => { setIgst(e.target.value); setTypoNote(""); }}
                 style={{ ...INPUT, width: 90 }} />
          <input data-testid="duty-publish-from" type="date" min={today} value={from}
                 onChange={(e) => { setFrom(e.target.value); setTypoNote(""); }} style={INPUT} />
          <button data-testid="duty-publish-submit" disabled={busy}
                  onClick={() => void publish(false)}
                  style={{ ...PRIMARY, cursor: busy ? "not-allowed" : "pointer" }}>
            {busy ? "Publishing…" : "Publish rate"}
          </button>
          {current && (
            <span data-testid="duty-publish-current" style={NOTE}>
              In force today: BCD {current.bcdPct} / SWS {current.swsPct} / IGST {current.igstPct}
            </span>
          )}
        </div>
        {typoNote && (
          <div data-testid="duty-publish-typo" role="alert"
               style={{ ...NOTE, color: semanticColors.warning ?? semanticColors.textPrimary }}>
            {typoNote}{" "}
            <button data-testid="duty-publish-confirm" onClick={() => void publish(true)} style={BUTTON}>
              Yes, publish
            </button>
          </div>
        )}
        {error && <div data-testid="duty-publish-error" role="alert"
                       style={{ ...NOTE, color: semanticColors.destructive }}>{error}</div>}
        {done && <div data-testid="duty-publish-done" style={NOTE}>{done}</div>}
      </div>
      )}

      {uploadOpen && (
      <div data-testid="duty-upload" style={BOX}>
        <div style={NOTE}>
          <span style={{ fontWeight: typography.fontWeight.medium, color: semanticColors.textPrimary }}>
            Upload rates (CSV or Excel).
          </span>{" "}
          Columns: HSN, BCD %, SWS %, IGST %, effective_from. You see what each row would do
          first; nothing is saved until you confirm, and confirming saves every row or none.
        </div>
        <input data-testid="duty-upload-file" type="file" accept=".csv,.xlsx"
               onChange={(e) => { void onFile(e.target.files?.[0]); e.target.value = ""; }} />
        {uploadError && <div data-testid="duty-upload-error" role="alert"
                             style={{ ...NOTE, color: semanticColors.destructive }}>{uploadError}</div>}
        {uploadDone && <div data-testid="duty-upload-done" style={NOTE}>{uploadDone}</div>}
        {preview && (
          <div data-testid="duty-upload-preview">
            <table style={{ borderCollapse: "collapse", width: "100%" }}>
              <thead><tr>
                {["#", "HSN", "BCD %", "SWS %", "IGST %", "From", "Result"].map((h) => <th key={h} style={TH}>{h}</th>)}
              </tr></thead>
              <tbody>
                {preview.map((r) => {
                  const input = (r.input ?? {}) as Record<string, unknown>;
                  const cell = (v: unknown) => (v === undefined || v === null ? "" : String(v));
                  return (
                    <tr key={r.index} data-testid={`duty-upload-row-${r.index}`} data-status={r.status}>
                      <td style={TD}>{r.index + 1}</td>
                      <td style={TD}>{r.hsn ?? cell(input.hsn)}</td>
                      <td style={TD}>{r.bcd_pct ?? cell(input.bcd_pct)}</td>
                      <td style={TD}>{r.sws_pct ?? cell(input.sws_pct)}</td>
                      <td style={TD}>{r.igst_pct ?? cell(input.igst_pct)}</td>
                      <td style={TD}>{r.effective_from ?? cell(input.effective_from)}</td>
                      <td data-testid={`duty-upload-result-${r.index}`}
                          style={{ ...TD, whiteSpace: "normal",
                                   color: r.status === "error" ? semanticColors.destructive : semanticColors.textPrimary }}>
                        {r.status === "error" ? `error: ${r.detail}` : r.detail}
                        {r.large_change ? ` — combined duty moves ${r.change_pct}%` : ""}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ display: "flex", gap: spacing[8], alignItems: "center", marginTop: spacing[8], flexWrap: "wrap" }}>
              {flagged > 0 && errorCount === 0 && (
                <label style={NOTE}>
                  <input data-testid="duty-upload-ack" type="checkbox" checked={ack}
                         onChange={(e) => setAck(e.target.checked)} />{" "}
                  {flagged} row{flagged === 1 ? "" : "s"} move the combined duty by more than {TYPO_GUARD_PCT}% — I have checked them
                </label>
              )}
              {errorCount > 0 && (
                <span data-testid="duty-upload-blocked" style={{ ...NOTE, color: semanticColors.destructive }}>
                  {errorCount} row{errorCount === 1 ? "" : "s"} with errors — fix the file and upload again. Nothing will be saved.
                </span>
              )}
              <button data-testid="duty-upload-confirm" disabled={!canConfirm} onClick={() => void confirm()}
                      style={{ ...PRIMARY, opacity: canConfirm ? 1 : 0.5, cursor: canConfirm ? "pointer" : "not-allowed" }}>
                {confirming ? "Saving…" : `Confirm and save ${preview.length} row${preview.length === 1 ? "" : "s"}`}
              </button>
              <button data-testid="duty-upload-cancel" style={BUTTON}
                      onClick={() => { setPreview(null); setAck(false); }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
      )}

      {fixDone && <div data-testid="duty-fix-done" style={{ ...NOTE, marginBottom: spacing[8] }}>{fixDone}</div>}
      {loading && <div style={NOTE}>Loading…</div>}
      {loadError && <div data-testid="duty-load-error" role="alert"
                         style={{ ...NOTE, color: semanticColors.destructive }}>{loadError}</div>}
      {!loading && !loadError && (
        <>
          {/* Search and filter directly above the history. */}
          <div data-testid="duty-filter-bar" className="flex items-center"
               style={{
                 minHeight: heights.filterBar, padding: `${spacing[6]}px ${spacing[12]}px`, marginBottom: spacing[8],
                 backgroundColor: semanticColors.panelBg, border: `1px solid ${semanticColors.border}`,
                 borderRadius: radius.base, gap: spacing[12], flexWrap: "wrap",
               }}>
            <input data-testid="duty-search" placeholder="Search HSN" value={search}
                   onChange={(e) => setSearch(e.target.value)} style={{ ...INPUT, flex: 1, minWidth: 160 }} />
            <select data-testid="duty-filter-standing" value={standingFilter}
                    onChange={(e) => setStandingFilter(e.target.value)} style={INPUT}>
              {["All", "In force", "Starts later", "Ended", "Withdrawn"].map((o) =>
                <option key={o} value={o}>{o === "All" ? "Status: All" : o}</option>)}
            </select>
            <span style={{ ...NOTE, whiteSpace: "nowrap" }}>
              <span data-testid="duty-count" className="tabular-nums">{shown.length}</span>
              {shown.length !== rates.length ? <> of {rates.length}</> : null} rates
            </span>
          </div>

          {/* The history, in the PAGE scroll: sticky header, no vertical scroller
              of its own; sideways only when the columns do not fit. */}
          <div ref={tableRef} data-testid="duty-rates-scroll" data-sideways={sideways ? "true" : "false"}
               style={{ border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
                        backgroundColor: semanticColors.surface,
                        overflowX: sideways ? "auto" : "visible", overflowY: "visible" }}>
            <table data-testid="duty-rates-table" style={{ borderCollapse: "separate", borderSpacing: 0, width: "100%", minWidth: TABLE_MIN }}>
              <thead data-testid="duty-rates-header"><tr>
                {["HSN", "BCD %", "SWS %", "IGST %", "From", "To", "Status", "Published by", ""].map((h) => (
                  <th key={h} style={{ ...TH, position: sideways ? "static" : "sticky", top: 0, zIndex: 1,
                                       backgroundColor: semanticColors.panelBg }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {shown.map((r) => {
                  const st = standing(r, today);
                  return (
                    <tr key={r.id} data-testid={`duty-row-${r.id}`} data-hsn={r.hsnCode} data-standing={st}>
                      <td style={TD}>{r.hsnCode}</td>
                      <td style={TD}>{r.bcdPct}</td>
                      <td style={TD}>{r.swsPct}</td>
                      <td style={TD}>{r.igstPct}</td>
                      <td style={TD}>{r.validFrom ? formatDate(r.validFrom) : "—"}</td>
                      <td style={TD}>{r.validTo ? formatDate(r.validTo) : "open"}</td>
                      <td style={TD}>{st}</td>
                      <td style={TD}>{r.createdBy ?? "Unknown"}</td>
                      <td style={{ ...TD, textAlign: "right" }}>
                        {/* Only on a row the SERVER says is correctable. */}
                        {r.correctable && (
                          <button data-testid={`duty-correct-${r.id}`} style={BUTTON} onClick={() => startFix(r)}>Correct</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {shown.length === 0 && (
                  <tr><td colSpan={9} data-testid="duty-rates-empty" style={{ ...TD, color: semanticColors.textMuted }}>
                    No duty rates match.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {fixing && (
        <div data-testid="duty-correct-backdrop"
             onClick={(e) => { if (e.target === e.currentTarget) setFixing(null); }}
             style={{ position: "fixed", inset: 0, zIndex: 50, backgroundColor: semanticColors.overlay,
                      display: "flex", alignItems: "center", justifyContent: "center", padding: spacing[16] }}>
          <div data-testid="duty-correct-dialog" role="dialog" aria-modal="true" aria-label="Correct duty rate"
               style={{ width: layout.modalWidth, maxWidth: "100%", backgroundColor: colors.white,
                        borderRadius: radius.base, boxShadow: shadows.modal, padding: spacing[20],
                        display: "flex", flexDirection: "column", gap: spacing[12] }}>
            <div style={{ fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.semibold }}>
              Correct a mistyped duty rate
            </div>
            <div style={NOTE}>
              HSN <span data-testid="duty-correct-hsn">{fixing.hsnCode}</span>, from{" "}
              {fixing.validFrom ? formatDate(fixing.validFrom) : "—"}. Now BCD {fixing.bcdPct} / SWS {fixing.swsPct} / IGST {fixing.igstPct}.
              Only the three percentages change; the server re-checks that this rate has not started
              and that no finalised quote used it.
            </div>
            <div style={{ display: "flex", gap: spacing[8], flexWrap: "wrap", alignItems: "center" }}>
              <label style={NOTE}>BCD % <input data-testid="duty-fix-bcd" style={{ ...INPUT, width: 80 }} value={fixBcd} onChange={(e) => setFixBcd(e.target.value)} /></label>
              <label style={NOTE}>SWS % <input data-testid="duty-fix-sws" style={{ ...INPUT, width: 80 }} value={fixSws} onChange={(e) => setFixSws(e.target.value)} /></label>
              <label style={NOTE}>IGST % <input data-testid="duty-fix-igst" style={{ ...INPUT, width: 80 }} value={fixIgst} onChange={(e) => setFixIgst(e.target.value)} /></label>
            </div>
            {fixError && <div data-testid="duty-fix-error" role="alert"
                              style={{ ...NOTE, color: semanticColors.destructive }}>{fixError}</div>}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: spacing[8] }}>
              <button data-testid="duty-fix-cancel" style={BUTTON} onClick={() => setFixing(null)}>Cancel</button>
              <button data-testid="duty-fix-save" style={PRIMARY} onClick={() => void saveFix(fixing)}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
